import axios from 'axios';
import API_BASE_URL from '../config';

// Create a separate axios instance for file uploads (without JSON content-type)
const uploadApi = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token for uploads
uploadApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors for uploads
uploadApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(email, password) {
    try {
      // Use .php extension to avoid .htaccess rewrite issues with POST requests
      const response = await api.post('/auth/login.php', { email, password });
      return {
        token: response.data.token,
        user: response.data.user
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  },

  async register(userData) {
    try {
      // Use .php extension to avoid .htaccess rewrite issues with POST requests
      const response = await api.post('/auth/register.php', userData);
      return {
        token: response.data.token,
        user: response.data.user
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  },

  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch profile');
    }
  },

  async updateProfile(userData) {
    try {
      const response = await api.put('/auth/profile', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  async uploadPDF(file) {
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      
      // Get user ID from localStorage
      const user = this.getCurrentUser();
      if (user && user.id) {
        formData.append('userId', user.id);
      }
      
      const token = localStorage.getItem('token');
      const response = await uploadApi.post('/auth/upload_pdf.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload PDF');
    }
  },

  async saveBookInfo(bookData) {
    try {
      // Add userId to request body as fallback
      const user = this.getCurrentUser();
      const requestData = {
        ...bookData,
        userId: user?.id || null
      };
      
      const response = await api.post('/auth/save_book_info.php', requestData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to save book information');
    }
  },

  async uploadCoverImage(file) {
    try {
      const formData = new FormData();
      formData.append('coverImage', file);
      
      // Get user ID from localStorage
      const user = this.getCurrentUser();
      if (user && user.id) {
        formData.append('userId', user.id);
      }
      
      const token = localStorage.getItem('token');
      const response = await uploadApi.post('/auth/upload_cover_image.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload cover image');
    }
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  isAuthenticated() {
    const token = localStorage.getItem('token');
    return !!token;
  },

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

export default api;
